import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { useState } from 'react';
import { feedback } from 'react-feedbacker';
import { useForm } from 'react-hook-form';
import { useMutation } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  NakedForm,
  PlayersSearch,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import { assert } from '@/utils/error';

const query = graphql`
  query SanityChallengePlayerRemoveForm {
    sanityChallengePlayerRemoveForm {
      title {
        ...SanityLocaleString
      }
    }
  }
`;

const removePlayersFromChallengeMutation = gql`
  mutation RemovePlayersFromChallenge(
    $playerIds: [String!]!
    $challengeId: String!
  ) {
    unassignPlayersFromChallenge(
      playerIds: $playerIds
      challengeId: $challengeId
    )
  }
`;

type Props = {
  challengeId: string | undefined;
  challengeName: string | undefined;
};

type FormValues = {
  playerIds: string[];
};

const ChallengePlayerRemoveForm = ({ challengeId, challengeName }: Props) => {
  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();
  const { close } = useModalContext();

  const staticData =
    useStaticQuery<Queries.SanityChallengePlayerRemoveFormQuery>(query);

  const form = staticData.sanityChallengePlayerRemoveForm;
  assert(form, 'missing form data');

  const [removePlayersFromChallengeState, removePlayersFromChallenge] =
    useMutation(removePlayersFromChallengeMutation);

  const defaultValues: FormValues = {
    playerIds: [],
  };

  const methods = useForm<FormValues>({ defaultValues });

  const onSubmit = (values: FormValues) => {
    const variables = {
      challengeId,
      ...values,
    };

    return removePlayersFromChallenge(variables).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        feedback.success('Successfully removed!');
        close();
      }
    });
  };

  if (!form) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={`Remove Player(s) from challenge: ${challengeName}`}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="flex p-6">
        <NakedForm className="w-full" methods={methods} onSubmit={onSubmit}>
          <div className="w-full sm:w-1/3">
            <PlayersSearch
              name="playerIds"
              id="ChallengePlayerRemoveForm__playerIds"
              title="Players"
            />
          </div>
          <ErrorMessage message={errorMessage} />
          <div className="flex justify-end">
            <SubmitButton
              value="Remove"
              disabled={removePlayersFromChallengeState.fetching}
            />
          </div>
        </NakedForm>
      </div>
    </Card>
  );
};

export default ChallengePlayerRemoveForm;
